import React, { useEffect, useMemo, useState } from 'react';
import { Image, Layout, Typography } from 'antd';
import {
  LogoutOutlined,
  FolderOpenOutlined,
  DatabaseOutlined,
  MenuOutlined,
  EditOutlined,
  CheckOutlined,
  TeamOutlined,
  BlockOutlined,
  DollarCircleOutlined,
  CalendarOutlined,
  ZoomInOutlined,
  LineChartOutlined,
  LinkOutlined,
  HomeOutlined,
  BellOutlined,
  LockOutlined,
  StarOutlined,
  SafetyCertificateOutlined,
  FlagOutlined,
  FolderOutlined,
  SearchOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/store';
import { logout } from '../slices/auth';
import logo from '../assets/logoSonarNavbar.png';
import { NavBar } from './nav/NavBar';
import { NavItemProps } from './nav/NavItem';
import { Header } from 'antd/lib/layout/layout';
import { theme } from 'src/constants/theme';

const { Content, Sider } = Layout;

interface Props {
  children: React.ReactNode;
  isNavBarHidden?: boolean;
}

const useStyles = createUseStyles({
  sider: {
    overflowY: 'auto',
    height: '100vh',
    left: 0,
    width: '241px !important',
    maxWidth: '18.375% !important',
    flex: 'none !important',
    backgroundColor: '#000080',
    '& .ant-menu-inline, .ant-menu-vertical, .ant-menu-vertical-left': {
      borderRight: '1px solid transparent',
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100% - 20vw)',
    },
  },
  logo: {
    filter: 'brightness(0) invert(1)',
    cursor: 'pointer',
  },
  logoText: {
    lineHeight: 1,
    fontSize: '2vw',
    fontWeight: '600',
    display: 'block',
    marginBottom: '10%',
    color: theme.palette.primary,
    fontFamily: "'Baloo 2'",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  mobileSiderTitle: {
    margin: '20px',
    color: theme.palette.common.white,
  },
  icon: {
    color: 'white',
    fontSize: '17px !important',
  },
  iconRed: {
    color: 'red',
    fontSize: '16px !important',
  },
  siteLayout: {
    background: '#fcfcfc',
    position: 'relative',
  },
  dashboardLogoImage: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'top right',
    pointerEvents: 'none',
    zIndex: 0,
  },
  label: {
    fontSize: '14px',
    color: 'white',
    lineHeight: '14.7px',
  },
  labelRed: {
    color: 'red',
    fontSize: '14px',
    // position: 'relative',
    // bottom: '10.53%',
  },
  mainLabel: {
    fontSize: '0.8vw',
    width: '100%',
    textAlign: 'center',
    position: 'relative',
    right: '0.4vw',
  },
  subLabel: {
    fontSize: '0.6vw',
    width: '100%',
    textAlign: 'center',
  },
  newProject: {
    border: '0.1vw dashed black',
    width: '13vw',
    height: '7vw',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    alignContent: 'center',
    gap: '0.5vw',
  },
});

export const LoggedLayout: React.VFC<Props> = ({
  children,
  isNavBarHidden,
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { user } = useAppSelector(({ auth: { user } }) => ({
    user,
  }));

  const [collapsed, setCollapsed] = useState<boolean>(window.innerWidth <= 768);
  const [width, setWidth] = useState<number>(window.innerWidth);
  function handleWindowSizeChange() {
    const windowWidth = window.innerWidth;
    setCollapsed(windowWidth <= 768);
    setWidth(windowWidth);
  }
  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  function refreshPage() {
    window.location.reload();
    return true;
  }

  const navItems = useMemo<NavItemProps[]>(() => {
    switch (user?.role) {
      case 'contributor':
        return [
          {
            navKey: 'donorsToQualify',
            route: '/',
            icon: <EditOutlined className={classes.icon} />,
            label: <span className={classes.label}>À qualifier</span>,
            onClick: () => isMobile && setCollapsed(true),
          },
          {
            navKey: 'donorsToValidate',
            route: '/donorsToValidate',
            icon: <DatabaseOutlined className={classes.icon} />,
            label: <span className={classes.label}>À valider</span>,
            onClick: () => isMobile && setCollapsed(true),
          },
          {
            navKey: 'validatedDonors',
            route: '/validatedDonors',
            icon: <CheckOutlined className={classes.icon} />,
            label: <span className={classes.label}>Validés</span>,
            onClick: () => isMobile && setCollapsed(true),
          },
          {
            navKey: 'donorsNotRelevant',
            route: '/donorsNotRelevant',
            icon: <DatabaseOutlined className={classes.icon} />,
            label: <span className={classes.label}>Non pertinent</span>,
            onClick: () => isMobile && setCollapsed(true),
          },
          {
            navKey: 'logout',
            icon: <LogoutOutlined className={classes.iconRed} />,
            label: <span className={classes.labelRed}>Déconnexion</span>,
            onClick: () => dispatch(logout()),
          },
        ];
      default:
        return [
          // {
          //   navKey: 'dashboard',
          //   route: '/',
          //   icon: <DashboardOutlined className={classes.icon} />,
          //   label: <span className={classes.label}>Tableau de bord</span>,
          //   onClick: () => isMobile && setCollapsed(true),
          // },
          ...(user?.role !== 'admin' && user?.ngoId
            ? [
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'title1',
                      },
                    ]
                  : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'ngo',
                        route: `/ngo/${user?.ngoId}/bypass`,
                        icon: <HomeOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>Organisation</span>
                        ),
                        onClick: () => isMobile && setCollapsed(true),
                      },
                    ]
                  : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'projects',
                        route: '/projects',
                        icon: <FolderOpenOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>Tableau de bord</span>
                        ),
                        onClick: () => isMobile && setCollapsed(true),
                      },
                    ]
                  : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'followedDonors',
                        route: `/ngo/followedDonors`,
                        icon: <BellOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>Abonnements</span>
                        ),
                        onClick: () => isMobile && setCollapsed(true),
                      },
                    ]
                  : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'freeSearch',
                        route: '/ngo/free-search',
                        icon: <SearchOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>Recherche Libre</span>
                        ),
                        onClick: () => isMobile && setCollapsed(true),
                      },
                    ]
                  : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'activeCallForTenders',
                        route: '/ngo/active-call-for-tenders',
                        icon: <FileSearchOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>
                            Programmes de soutien
                          </span>
                        ),
                        onClick: () => isMobile && setCollapsed(true),
                      },
                    ]
                  : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'premium',
                        route: `/ngo/premium`,
                        icon: <LockOutlined className={classes.icon} />,
                        label: <span className={classes.label}>Premium</span>,
                        onClick: () => isMobile && setCollapsed(true),
                      },
                    ]
                  : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'title2',
                      },
                    ]
                  : []),

                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'whySonar',
                        route: '/',
                        icon: <ZoomInOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>Pourquoi Sonar</span>
                        ),
                        onClick: () =>
                          window.open(
                            'https://www.sonar.so/pourquoi-sonar',
                            '_blank',
                          ),
                      },
                    ]
                  : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'demo',
                        route: '/',
                        icon: <CalendarOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>
                            Prendre Rendez-vous
                          </span>
                        ),
                        onClick: () =>
                          window.open(
                            'https://sonar.so/prendre-un-rendez-vous/',
                            '_blank',
                          ),
                      },
                    ]
                  : []),
                // ...(user?.ngoId
                //   ? [{ navKey: 'divider2', isDivider: false }]
                //   : []),
                // ...(user?.ngoId
                //   ? [
                //       {
                //         navKey: 'title3',
                //       },
                //     ]
                //   : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'incontournable',
                        route: '/',
                        icon: <StarOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>
                            Un levier incontournable
                          </span>
                        ),
                        onClick: () =>
                          window.open(
                            'https://www.sonar.so/un-levier-incontournable',
                            '_blank',
                          ),
                      },
                    ]
                  : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'title3',
                      },
                    ]
                  : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'generalInterests',
                        route: '/',
                        icon: (
                          <SafetyCertificateOutlined className={classes.icon} />
                        ),
                        label: (
                          <span className={classes.label}>
                            L&apos;intérêt général
                          </span>
                        ),
                        onClick: () =>
                          window.open(
                            'https://sonar.so/interet-general/',
                            '_blank',
                          ),
                      },
                    ]
                  : []),

                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'fisc2',
                        route: '/',
                        icon: <FlagOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>
                            Le rescrit fiscal
                          </span>
                        ),
                        onClick: () =>
                          window.open(
                            'https://sonar.so/le-rescrit-fiscal/',
                            '_blank',
                          ),
                      },
                    ]
                  : []),
                ...(user?.ngoId
                  ? [
                      {
                        navKey: 'fisc3',
                        route: '/',
                        icon: <FolderOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>Le reçu fiscal</span>
                        ),
                        onClick: () =>
                          window.open(
                            'https://sonar.so/le-recu-fiscal/',
                            '_blank',
                          ),
                      },
                    ]
                  : []),

                // navKey === 'indispensables' ? (
                //   <Popover
                //     placement="bottom"
                //     key={index}
                //     content={
                //       <>
                //         <NavItem
                //           key={'fisc'}
                //           navKey={'fisc'}
                //           route={'/'}
                //           label={
                //             <span className={classes.label}>Le reçu fiscal</span>
                //           }
                //           onClick={() =>
                //             window.open(
                //               'https://sonar.so/le-recu-fiscal/',
                //               '_blank',
                //             )
                //           }
                //         />
                //       </>
                //     }
                //   >
                //     <NavItem key={index} navKey={navKey} {...rest} />
                //   </Popover>
                // ) :

                // ...(user?.ngoId
                //   ? [
                //       {
                //         navKey: 'resources',
                //         route: '/',
                //         icon: <FileDoneOutlined className={classes.icon} />,
                //         label: (
                //           <span className={classes.label}>Ressources</span>
                //         ),
                //         onClick: () =>
                //           window.open(
                //             'https://sonar.so/ressources-porteurs-de-projet/',
                //             '_blank',
                //           ),
                //       },
                //     ]
                //   : []),
              ]
            : []),

          // ...(user?.ngoId ? [{ navKey: 'divider3', isDivider: true }] : []),

          ...(user?.role !== 'admin' && user?.donorId
            ? [
                ...(user?.donorId
                  ? [
                      {
                        navKey: 'title1',
                      },
                    ]
                  : []),
                ...(user?.donorId
                  ? [
                      {
                        navKey: 'donor',
                        route: `/donor/${user?.donorId}/bypass`,
                        icon: <HomeOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>
                            Mon organisation
                          </span>
                        ),
                        onClick: () => isMobile && setCollapsed(true),
                      },
                    ]
                  : []),
                ...(user?.donorId
                  ? [
                      {
                        navKey: 'callForTenders',
                        route: '/callForTenders',
                        icon: <FolderOpenOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>Tableau de bord</span>
                        ),
                        onClick: () => isMobile && setCollapsed(true),
                      },
                    ]
                  : []),
                ...(user?.donorId
                  ? [
                      {
                        navKey: 'title2',
                      },
                    ]
                  : []),

                ...(user?.donorId
                  ? [
                      {
                        navKey: 'takeMeeting',
                        route: '/',
                        icon: <CalendarOutlined className={classes.icon} />,
                        label: (
                          <span className={classes.label}>
                            Prendre rendez-vous
                          </span>
                        ),
                        onClick: () =>
                          window.open(
                            'https://sonar.so/prendre-un-rendez-vous/',
                            '_blank',
                          ),
                      },
                      ...(user?.donorId
                        ? [
                            {
                              navKey: 'featuresDonors',
                              route: '/',
                              icon: <ZoomInOutlined className={classes.icon} />,
                              label: (
                                <span className={classes.label}>
                                  Fonctionnalités
                                </span>
                              ),
                              onClick: () =>
                                window.open(
                                  'https://sonar.so/fonctionnalites-mecenes/',
                                  '_blank',
                                ),
                            },
                          ]
                        : []),
                      // ...(user?.donorId
                      //   ? [
                      //       {
                      //         navKey: 'resourcesDonor',
                      //         route: '/',
                      //         icon: (
                      //           <FileDoneOutlined className={classes.icon} />
                      //         ),
                      //         label: (
                      //           <span className={classes.label}>
                      //             Ressources
                      //           </span>
                      //         ),
                      //         onClick: () =>
                      //           window.open(
                      //             'https://sonar.so/ressources-mecenes/',
                      //             '_blank',
                      //           ),
                      //       },
                      //     ]
                      //   : []),
                    ]
                  : []),
              ]
            : []),

          // { navKey: 'divider1', isDivider: true },
          ...(user?.role === 'admin'
            ? [
                // { navKey: 'divider2', isDivider: false },
                {
                  navKey: 'title1',
                },
                {
                  navKey: 'database',
                  route: '/database',
                  icon: <DatabaseOutlined className={classes.icon} />,
                  label: <span className={classes.label}>M. FR en ligne</span>,
                  onClick: () => isMobile && setCollapsed(true),
                },
                {
                  navKey: 'users/ngo',
                  route: '/users/ngo',
                  icon: <TeamOutlined className={classes.icon} />,
                  label: (
                    <span className={classes.label}>PP. Utilisateurs</span>
                  ),
                  onClick: () =>
                    refreshPage() && isMobile && setCollapsed(true),
                },
                {
                  navKey: 'users/donor',
                  route: '/users/donor',
                  icon: <TeamOutlined className={classes.icon} />,
                  label: <span className={classes.label}>M. Utilisateurs</span>,
                  onClick: () =>
                    refreshPage() && isMobile && setCollapsed(true),
                },
                {
                  navKey: 'wachete',
                  route: '/wachete',
                  icon: <LinkOutlined className={classes.icon} />,
                  label: <span className={classes.label}>Wachete</span>,
                  onClick: () => isMobile && setCollapsed(true),
                },
                {
                  navKey: 'statistics/ngo',
                  route: '/statistics/ngo',
                  icon: <LineChartOutlined className={classes.icon} />,
                  label: <span className={classes.label}>PP. Stat</span>,
                  onClick: () =>
                    refreshPage() && isMobile && setCollapsed(true),
                },
                {
                  navKey: 'statistics/donor',
                  route: '/statistics/donor',
                  icon: <LineChartOutlined className={classes.icon} />,
                  label: <span className={classes.label}>M. Stat</span>,
                  onClick: () =>
                    refreshPage() && isMobile && setCollapsed(true),
                },
                {
                  navKey: 'adminNgos',
                  route: '/adminNgos',
                  icon: <DatabaseOutlined className={classes.icon} />,
                  label: (
                    <span className={classes.label}>Porteurs de projets</span>
                  ),
                  onClick: () => isMobile && setCollapsed(true),
                },
                {
                  navKey: 'financing-needs',
                  route: '/financing-needs',
                  icon: <DollarCircleOutlined className={classes.icon} />,
                  label: <span className={classes.label}>Projets</span>,
                  onClick: () => isMobile && setCollapsed(true),
                },
                {
                  navKey: 'donors',
                  route: '/donors',
                  icon: <DatabaseOutlined className={classes.icon} />,
                  label: <span className={classes.label}>Mécènes</span>,
                  onClick: () => isMobile && setCollapsed(true),
                },
                {
                  navKey: 'callForTendersAdmin',
                  route: '/callForTendersAdmin',
                  icon: <DollarCircleOutlined className={classes.icon} />,
                  label: (
                    <span className={classes.label}>Programmes de soutien</span>
                  ),
                  onClick: () => isMobile && setCollapsed(true),
                },
                { navKey: 'dividerAdmin', isDivider: true },
                {
                  navKey: 'contributors',
                  route: '/contributors',
                  icon: <TeamOutlined className={classes.icon} />,
                  label: <span className={classes.label}>Contributeurs</span>,
                  onClick: () => isMobile && setCollapsed(true),
                },
                {
                  navKey: 'donorsToQualify',
                  route: '/donorsToQualify',
                  icon: <DatabaseOutlined className={classes.icon} />,
                  label: <span className={classes.label}>À assigner</span>,
                  onClick: () => isMobile && setCollapsed(true),
                },
                {
                  navKey: 'assignedDonors',
                  route: '/assignedDonors',
                  icon: <DatabaseOutlined className={classes.icon} />,
                  label: <span className={classes.label}>Assignées</span>,
                  onClick: () => isMobile && setCollapsed(true),
                },
                {
                  navKey: 'donorsToValidate',
                  route: '/donorsToValidate',
                  icon: <DatabaseOutlined className={classes.icon} />,
                  label: <span className={classes.label}>À valider</span>,
                  onClick: () => isMobile && setCollapsed(true),
                },
                {
                  navKey: 'donorsNotRelevant',
                  route: '/donorsNotRelevant',
                  icon: <DatabaseOutlined className={classes.icon} />,
                  label: <span className={classes.label}>Non pertinent</span>,
                  onClick: () => isMobile && setCollapsed(true),
                },
                {
                  navKey: 'donorsMergeDuplicates',
                  route: '/donors-merge-duplicates',
                  icon: <BlockOutlined className={classes.icon} />,
                  label: (
                    <span className={classes.label}>Fusion des doublons</span>
                  ),
                  onClick: () => isMobile && setCollapsed(true),
                },
                {
                  navKey: 'ngosMergeDuplicates',
                  route: '/ngos-merge-duplicates',
                  icon: <BlockOutlined className={classes.icon} />,
                  label: (
                    <span className={classes.label}>
                      Fusion des associations
                    </span>
                  ),
                  onClick: () => isMobile && setCollapsed(true),
                },
              ]
            : []),
          // {
          //   navKey: 'services',
          //   route: '/services',
          //   icon: <CustomerServiceOutlined className={classes.icon} />,
          //   label: <span className={classes.label}>Aide à la levée</span>,
          //   onClick: () => isMobile && setCollapsed(true),
          // },
          // TODO: confirm if completely removing settings
          // ...(user?.ngoId
          //   ? [
          //       {
          //         navKey: 'settings',
          //         route: '/settings',
          //         icon: <SettingOutlined className={classes.icon} />,
          //         label: <span className={classes.label}>Réglages</span>,
          //         onClick: () => isMobile && setCollapsed(true),
          //       },
          //     ]
          //   : []),
          // divider
          // { navKey: 'bottomDivider', isDivider: true },
          {
            navKey: 'logoutDivider',
            isDivider: true,
          },
          {
            navKey: 'logout',
            icon: <LogoutOutlined className={classes.iconRed} />,
            label: <span className={classes.labelRed}>Déconnexion</span>,
            onClick: () => dispatch(logout()),
          },
        ];
    }
  }, [user]);

  const isMobile = useMemo(() => {
    return width <= 768;
  }, [width]);

  return (
    <Layout style={{ height: '100%', background: theme.palette.background }}>
      {(user?.ngoId || user?.role === 'contributor' || !user?.ngoId) && (
        <Sider
          className={classes.sider}
          collapsedWidth={'0'}
          collapsed={collapsed}
          theme={'light'}
          collapsible
          trigger={null}
          hidden={isNavBarHidden ? true : false}
        >
          {!isMobile ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '241px',
                height: '50px',
                marginTop: '66px',
              }}
            >
              <img
                alt="logo"
                src={logo}
                width={68}
                height={50}
                className={classes.logo}
                onClick={() => history.push('/')}
              />
            </div>
          ) : (
            <Typography className={classes.mobileSiderTitle} />
          )}
          <NavBar navItems={navItems} />
        </Sider>
      )}
      <Layout className={classes.siteLayout}>
        <img
          src="/images/dashboardLogo.png"
          alt="Dashboard logo"
          className={classes.dashboardLogoImage}
        />
        {isMobile && (
          <Header style={{ padding: '10px' }}>
            <div className={classes.header}>
              <MenuOutlined
                style={{ color: 'white', fontSize: '1.875vw' }}
                height={'30'}
                onClick={() => {
                  setCollapsed(!collapsed);
                }}
              />
              <Image
                src={logo}
                width={30}
                preview={false}
                onClick={() => history.push('/')}
              />
            </div>
          </Header>
        )}
        <Content
          style={{
            boxSizing: 'border-box',
            height: '100%',
            overflow: 'auto',
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};
